import Fuse from 'fuse.js'

// Curated static quick-actions. Each entry exposes a logical `action` token
// that the UI maps to a side-effect (creating a new set/question/repo/section).
// Adding a new quick action is a single entry here plus a matching arm in
// `onSuggestionSelected` inside the top-nav search bar.
//
// Hits are rendered as Algolia-shaped objects with __quickAction: true and
// __localOrigin: true. They surface in two places:
//   1. The dropdown empty-state (focused, no query) — full list, no fuzz.
//   2. Mid-query results — fuzzy-matched against `label` + `keywords`.

export const STATIC_QUICK_ACTIONS = [
  {
    id: 'qa-new-set',
    label: 'New Set',
    icon: 'compose',
    action: 'newSet',
    keywords: ['create', 'add', 'set', 'new'],
  },
  {
    id: 'qa-new-pack',
    label: 'New Pack',
    icon: 'add-pack',
    action: 'newRepo',
    keywords: ['create', 'add', 'pack', 'repo', 'new'],
  },
  {
    id: 'qa-new-class',
    label: 'New Class',
    icon: 'add-class',
    action: 'newSection',
    keywords: ['create', 'add', 'class', 'section', 'new'],
  },
  {
    id: 'qa-upgrade-pro',
    label: 'Upgrade to Pro',
    icon: 'gear',
    action: 'upgradePro',
    keywords: ['pro', 'upgrade', 'billing', 'subscription', 'plan', 'premium', 'paid'],
  },
]

// Exported so the internal search-tuning sandbox can display/reset the defaults.
export const QUICK_ACTIONS_FUSE_OPTIONS = {
  threshold: 0.2,
  ignoreLocation: true,
  minMatchCharLength: 3,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'label', weight: 2 },
    { name: 'keywords', weight: 1 },
  ],
}

/**
 * Build a Fuse index over the quick-actions list. Caller is expected to
 * memoise the entries reference; this function is a thin allocator.
 */
export function buildQuickActionsFuseIndex(entries = STATIC_QUICK_ACTIONS) {
  return new Fuse(Array.isArray(entries) ? entries : [], QUICK_ACTIONS_FUSE_OPTIONS)
}

/**
 * Project a raw entry into the Algolia-shaped hit consumed by the dropdown.
 * Used for the empty-state list where no Fuse query is run.
 */
export function toQuickActionHit(entry) {
  return {
    ...entry,
    objectID: entry.id,
    __quickAction: true,
    __localOrigin: true,
  }
}

/**
 * Run a query against the Fuse quick-actions index and return Algolia-shaped
 * flat hits, each carrying __quickAction: true, __localOrigin: true.
 */
export function searchQuickActions(fuse, query, limit = 6) {
  if (!query || !fuse) return []
  return fuse.search(query, { limit }).map(({ item, score, matches }) => ({
    ...toQuickActionHit(item),
    __localScore: score,
    __localMatches: matches,
  }))
}
